// earth/world.js: HOME. Orbital sunrise over the limb -> night side, down to
// the visitor's own city lights (NASA Black Marble 2016) -> pull back to a
// pale blue dot. Visuals are a pure function of (progress, params, time, look).
import * as THREE from 'three';
import { FullScreenQuad, Blit, makeTarget, loadTexture, loadJSON, anchor, clamp, lerp, smooth, seg, tempToRGB } from '../lib/common.js';
import { FS_VERT, MAIN_FRAG, FX_FRAG } from './glsl.js';
import { rig, makeFrame, sunVisible, SUN_R, dirLL } from './rig.js';

const TIER = {
  high: { px: 1.3e6, pmax: 1.5, steps: 16, agn: 10, res: 1.0, day: 'day-4k', night: 'night-8k', clouds: 'clouds-2k', land: 'land-1k', stars: 170 },
  mid: { px: 0.6e6, pmax: 1.0, steps: 12, agn: 8, res: 0.75, day: 'day-4k', night: 'night-4k', clouds: 'clouds-2k', land: 'land-1k', stars: 150 },
  low: { px: 0.2e6, pmax: 0.6, steps: 8, agn: 6, res: 0.6, day: 'day-2k', night: 'night-2k', clouds: 'clouds-1k', land: 'land-1k', stars: 130 },
};
const withTimeout = (p, ms, fb) => Promise.race([p, new Promise((r) => setTimeout(() => r(fb), ms))]).catch(() => fb);
const blackTex = () => { const t = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1); t.needsUpdate = true; return t; };

export function create(renderer, opts = {}) {
  const T = TIER[opts.tier] || TIER.mid;
  const base = `${opts.base || ''}assets/earth/`;
  const camera = new THREE.PerspectiveCamera(40, 1, 1e-4, 1e5);
  const scene = new THREE.Scene();
  const sunC = tempToRGB(5800);
  const U = {
    uCamPos: { value: new THREE.Vector3() }, uSun: { value: new THREE.Vector3(0, 0, 1) }, uCity: { value: new THREE.Vector3() },
    uSunE: { value: new THREE.Vector3(sunC[0] * 2.8, sunC[1] * 2.8, sunC[2] * 2.8) },
    uInvProj: { value: new THREE.Matrix4() }, uCamWorld: { value: new THREE.Matrix4() },
    tDay: { value: null }, tNight: { value: null }, tClouds: { value: null }, tLand: { value: null }, tCity: { value: null },
    uCityB: { value: new THREE.Vector4(0, 1, 0, 1) }, uCityOn: { value: 0 },
    tCityL: { value: null }, uCityLOn: { value: 0 }, uCityKm: { value: new THREE.Vector2(470, 470) }, uMoon: { value: 0.012 }, uCityLook: { value: 0 }, uNightW: { value: 4096 },
    uLights: { value: 1.0 }, uNightAmb: { value: 0.03 }, uAirglow: { value: 0.0008 }, uClear: { value: 0.05 },
    uSunDisk: { value: 260 }, uSunR: { value: SUN_R }, uPix: { value: 0.001 }, uDisk: { value: 1 },
    uStars: { value: 0.25 }, uStarN: { value: T.stars }, uRough: { value: 0.0015 }, uAlb: { value: 1.0 }, uMulti: { value: 0.02 }, uAer: { value: 0.55e-3 }, uHz: { value: 1 }, uWarmL: { value: 1 },
  };
  const mainQ = new FullScreenQuad(new THREE.ShaderMaterial({
    defines: { STEPS: T.steps, AGN: T.agn }, uniforms: U, vertexShader: FS_VERT, fragmentShader: MAIN_FRAG,
    depthTest: false, depthWrite: false,
  }));
  const FU = {
    uRes: { value: new THREE.Vector2(1, 1) }, uSunPx: { value: new THREE.Vector2() }, uDotPx: { value: new THREE.Vector2() },
    uGlareC: { value: new THREE.Vector3(1, 0.9, 0.75) }, uDotC: { value: new THREE.Vector3(0.12, 0.27, 1.3) },
    uGlare: { value: 0 }, uDot: { value: 0 }, uDotS: { value: 1 }, uRot: { value: 0.2 }, uVeil: { value: 0 }, uBand: { value: 0 },
  };
  const fxQ = new FullScreenQuad(new THREE.ShaderMaterial({
    uniforms: FU, vertexShader: FS_VERT, fragmentShader: FX_FRAG,
    depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending,
  }));
  const blit = new Blit({ bicubic: true });
  let rt = null, W = 1, H = 1, cities = null, frame = null, fkey = '', cityKey = '', cityTex = null, cityLTex = null;
  const own = [];
  const rigOut = { pos: new THREE.Vector3(), fwd: new THREE.Vector3(), up: new THREE.Vector3(), fov: 40, dist: 1, flash: 0, sx: 0, sy: 0 };
  const tmp = new THREE.Vector3(), tmp2 = new THREE.Vector3();
  const grade = { exposure: 1, bloomK: 0.8, threshold: 1.1, sat: 1.05, contrast: 1.05, vignette: 0.3, grain: 0.03, ca: 0.0,
    shadows: [0.0, 0.035, 0.09], highs: [0.0, 0.012, 0.03], gainC: [0.95, 1.0, 1.05] };   // LIGHT.md: cool, no warm gain
  const anchors = { city: { x: 0.5, y: 0.5, visible: false }, sun: { x: 0.5, y: 0.3, visible: false }, sunrise: { x: 0.5, y: 0.3, visible: false },
    limb: { x: 0.5, y: 0.4, visible: false }, dot: { x: 0.5, y: 0.5, visible: false }, earth: { x: 0.5, y: 0.5, visible: false } };

  async function tex(name, srgb, red) {
    const t = await withTimeout(loadTexture(`${base}${name}.jpg`, { srgb, renderer, anisotropy: 8 }), 25000, null);
    if (!t) return blackTex();
    t.wrapS = THREE.RepeatWrapping;
    if (red) t.format = THREE.RedFormat;
    own.push(t);
    return t;
  }
  function pickCity(lat, lon) {
    if (!cities) return null;
    let best = null, bd = 1e9;
    const p = dirLL(lat, lon, tmp);
    for (const c of cities.cities) {
      const d = Math.acos(clamp(p.dot(dirLL(c.lat, c.lon, tmp2)), -1, 1)) * 6371;
      if (d < bd) { bd = d; best = c; }
    }
    return bd < 300 ? best : null;
  }
  function useCity(lat, lon) {
    const c = pickCity(lat, lon), key = c ? c.slug : '';
    if (key === cityKey) return;
    cityKey = key;
    U.uCityOn.value = 0; U.uCityLOn.value = 0;
    if (!c) return;
    const d = Math.PI / 180, b = c.bounds;
    const lt = (u) => loadTexture(u, { srgb: false, renderer, anisotropy: 8 });
    // city2/: coast-cleaned lights + land/moonlight map (tools/earth_city2.py); city/ is the fallback
    lt(`${base}city2/${c.slug}.jpg`).catch(() => lt(`${base}city/${c.slug}.jpg`)).then((t) => {
      if (cityKey !== key) { t.dispose(); return; }
      t.format = THREE.RedFormat;
      if (cityTex) cityTex.dispose();
      cityTex = t; U.tCity.value = t;
      U.uCityB.value.set(b[0] * d, b[1] * d, b[2] * d, b[3] * d);
      U.uCityKm.value.set((b[3] - b[2]) * d * Math.cos(c.lat * d) * 6371, (b[1] - b[0]) * d * 6371);
      U.uCityOn.value = 1;
    }).catch(() => {});
    lt(`${base}city2/${c.slug}-l.jpg`).then((t) => {
      if (cityKey !== key) { t.dispose(); return; }
      t.format = THREE.RedFormat;
      if (cityLTex) cityLTex.dispose();
      cityLTex = t; U.tCityL.value = t; U.uCityLOn.value = 1;
    }).catch(() => {});
  }

  function resize(w, h) {
    W = w; H = h;
    const pr = renderer.getPixelRatio() || 1;
    // internal buffer from a pixel budget per tier (CSS-px ratio clamped to [res, pmax]): desktops unchanged, phones sharper
    const kc = clamp(Math.sqrt(T.px / Math.max(1, (w / pr) * (h / pr))), T.res, T.pmax), k = Math.min(1, kc / pr);
    const iw = Math.max(2, Math.round(w * k)), ih = Math.max(2, Math.round(h * k));
    if (opts.half !== false) {
      if (!rt) rt = makeTarget(iw, ih, { half: true });
      else rt.setSize(iw, ih);
    }
    FU.uRes.value.set(w, h);
  }

  function update(dt, time, input) {
    const P = input.params || {};
    const lat = Number.isFinite(+P.lat) && P.lat !== '' ? +P.lat : 19.076, lon = Number.isFinite(+P.lon) && P.lon !== '' ? +P.lon : 72.878;
    const key = `${lat.toFixed(3)},${lon.toFixed(3)}`;
    if (key !== fkey) { fkey = key; frame = makeFrame(lat, lon); U.uSun.value.copy(frame.S); U.uCity.value.copy(frame.C); }
    useCity(lat, lon);
    const p = clamp(input.progress || 0, 0, 1), aspect = input.aspect || W / H;
    const o = rig(p, aspect, input.look || { x: 0, y: 0 }, frame, rigOut);
    camera.fov = o.fov; camera.aspect = W / H;
    camera.position.copy(o.pos); camera.up.copy(o.up);
    camera.lookAt(tmp.copy(o.pos).add(o.fwd));
    const sx = (+P.shiftX || 0) + o.sx, sy = (+P.shiftY || 0) + o.sy;
    if (sx || sy) camera.setViewOffset(W, H, -sx * W, sy * H, W, H); else camera.clearViewOffset();
    camera.updateProjectionMatrix(); camera.updateMatrixWorld();
    U.uCamPos.value.copy(o.pos);
    U.uInvProj.value.copy(camera.projectionMatrixInverse);
    U.uCamWorld.value.copy(camera.matrixWorld);
    const tanH = Math.tan(o.fov * Math.PI / 360);
    const ih = rt ? rt.height : H;
    U.uPix.value = 2 * tanH / ih;
    // planet size on screen -> hand over from the ray-traced disk to a point of light
    const rpx = Math.tan(Math.asin(Math.min(1, 1 / o.dist))) / tanH * H * 0.5;
    const disk = smooth(0.8, 4.0, rpx);
    U.uDisk.value = disk;
    // Sun: visibility over the limb, glare, flash residue from the ignition
    const vis = sunVisible(o.pos, frame.S);
    anchor(camera, tmp.copy(o.pos).addScaledVector(frame.S, 1000), anchors.sun);
    Object.assign(anchors.sunrise, anchors.sun); Object.assign(anchors.limb, anchors.sun);
    FU.uSunPx.value.set(anchors.sun.x * W, (1 - anchors.sun.y) * H);
    const inFront = tmp2.copy(frame.S).dot(o.fwd) > 0 ? 1 : 0;
    const rise = smooth(0, 1.6 * Math.PI / 180, Math.acos(clamp(-o.pos.dot(frame.S) / o.pos.length(), -1, 1)) - Math.asin(Math.min(1, 1.0019 / o.pos.length())));
    FU.uGlareC.value.set(1, lerp(0.84, 1.0, rise), lerp(0.74, 1.0, rise));   // white Sun core (only a touch warm at first contact)
    FU.uGlare.value = inFront * vis * (0.6 + 1.6 * o.flash) * (1 - smooth(0.62, 0.9, p) * 0.9);
    FU.uVeil.value = 1.2 * o.flash * o.flash * o.flash;
    U.uHz.value = (P.hz === undefined || P.hz === '' ? 1 : +P.hz) * smooth(0.1, 0.18, p);   // not in the sunrise frame   // QA A/B of the horizon unsharp
    U.uWarmL.value = 1 - smooth(0.1, 0.15, p);   // warm limb line only in the sunrise (p < .15)
    if (P.noGlare) { FU.uGlare.value = 0; FU.uVeil.value = 0; }   // QA only
    FU.uRot.value = 0.25 + 0.1 * p;
    // pale blue dot
    anchor(camera, tmp.set(0, 0, 0), anchors.dot); Object.assign(anchors.earth, anchors.dot);
    FU.uDotPx.value.set(anchors.dot.x * W, (1 - anchors.dot.y) * H);
    // min-size, min-brightness point (never vanishes when sub-pixel): ~1.5 CSS px core + soft halo
    const dpr = renderer.getPixelRatio() || 1;
    FU.uDot.value = (1 - smooth(1.5, 6.0, rpx)) * 2.2 * (anchors.dot.visible ? 1 : 0);   // fades in over the last few px of disk: no blink at the hand-over
    FU.uDotS.value = 0.62 * dpr * Math.max(1, H / dpr / 1100);
    FU.uBand.value = smooth(0.8, 0.87, p);
    // city label anchor (front hemisphere, not behind the limb)
    anchor(camera, frame.C, anchors.city);
    anchors.city.visible = anchors.city.visible && tmp.copy(o.pos).sub(frame.C).dot(frame.C) > 0 && p > 0.3 && p < 0.74;
    // exposure / look per beat. city: the ISS-photo close-up (crisp lights, little bloom, a touch more grain)
    const night = smooth(0.24, 0.42, p) * (1 - smooth(0.68, 0.8, p));
    const city = smooth(0.4, 0.5, p) * (1 - smooth(0.64, 0.74, p));
    U.uCityLook.value = smooth(0.38, 0.47, p) * (1 - smooth(0.64, 0.74, p));   // r7: city close-up look (near-black navy land, hero lights)
    grade.exposure = lerp(1.0, 1.12, night);
    const port = aspect < 1;   // phone: the city fills a narrow frame -> less glow
    grade.bloomK = lerp(lerp(0.8, 0.55, night), port ? 0.08 : 0.12, city);   // r7: .1/.2 -> .08/.12 (crisp points, less haze)
    grade.threshold = lerp(lerp(1.1, 1.05, night), port ? 2.6 : 2.3, city);   // r7: 2.4/1.9 -> 2.6/2.3
    grade.grain = lerp(0.03, 0.045, city);
    grade.sat = lerp(1.05, 1.4, U.uCityLook.value);   // v5.1: 2.05 turned every light neon (RGB speckle)   // r7: follows the city look (with the city ramp the lower sat turned the ramp-in sodium gold)
    U.uStars.value = lerp(0.16, 0.24, smooth(0.2, 0.35, p)) * (1 - 0.5 * smooth(0.79, 0.86, p)) * (opts.mobile ? 0.7 : 1);
    U.uLights.value = lerp(1.35, port ? 0.55 : 1.25, city);   // r7: brighter hero lights (r6 .4 : .5)
    U.uClear.value = lerp(0.05, 0.016, city);
    U.uMoon.value = lerp(lerp(0.012, 0.028, smooth(0.12, 0.26, p) * (1 - smooth(0.66, 0.76, p))), 0.052, city) * (1 - 0.5 * smooth(0.7, 0.85, p));
  }

  function render(target) {
    const ac = renderer.autoClear;
    if (rt) { renderer.setRenderTarget(rt); mainQ.render(renderer); blit.render(renderer, rt, target); }
    else { renderer.setRenderTarget(target); mainQ.render(renderer); }
    renderer.autoClear = false;
    renderer.setRenderTarget(target);
    fxQ.render(renderer);
    renderer.autoClear = ac;
  }

  return {
    scene, camera, grade, anchors,
    async load() {
      const [day, night, clouds, land, cj] = await Promise.all([
        tex(T.day, true, false), tex(T.night === 'night-8k' && renderer.capabilities.maxTextureSize < 8192 ? 'night-4k' : T.night, false, true), tex(T.clouds, false, true), tex(T.land, false, true),
        withTimeout(loadJSON(`${base}cities.json`), 15000, null),
      ]);
      U.tDay.value = day; U.tNight.value = night; U.tClouds.value = clouds; U.tLand.value = land;
      U.tCity.value = blackTex(); own.push(U.tCity.value); U.tCityL.value = U.tCity.value;
      U.uNightW.value = (night.image && night.image.width) || 4096;
      cities = cj && cj.cities ? cj : null;
    },
    resize, update, render,
    dispose() {
      for (const t of own) t.dispose();
      if (cityTex) cityTex.dispose();
      if (cityLTex) cityLTex.dispose();
      if (rt) rt.dispose();
      mainQ.material.dispose(); mainQ.dispose(); fxQ.material.dispose(); fxQ.dispose(); blit.dispose();
    },
  };
}
